import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import SortableList, { DragHandle } from "../../components/SortableList";

const BLANK_SLIDE = {
  media_type: "image", image_desktop: "", image_mobile: "", video_url: "", fallback_image: "",
  overlay_color: "#08111d", overlay_opacity: 0.55,
  title_tr: "", title_en: "", title_de: "",
  subtitle_tr: "", subtitle_en: "", subtitle_de: "",
  cta1_text: "", cta1_link: "", cta1_style: "gold",
  cta2_text: "", cta2_link: "", cta2_style: "outline",
  align: "left", duration_override: null, active: true, order: 0,
};

const BLANK_SETTINGS = {
  default_duration: 4, transition: "fade", transition_duration: 0.8,
  autoplay: true, pause_on_hover: true, show_arrows: true, show_dots: true,
};

export default function AdminSlideshow() {
  const [slides, setSlides] = useState([]);
  const [settings, setSettings] = useState(BLANK_SETTINGS);

  const load = () => api.get("/admin/slideshow").then((r) => {
    const ss = (r.data.slides || []).map((s, i) => ({ ...s, _key: s.id || `s-${i}` }));
    setSlides(ss);
    setSettings({ ...BLANK_SETTINGS, ...(r.data.settings || {}) });
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/admin/slideshow", {
        slides: slides.map(({ _key, ...s }, i) => ({ ...s, order: i + 1 })),
        settings,
      });
      toast.success("Slideshow kaydedildi"); load();
    } catch { toast.error("Hata"); }
  };

  const addSlide = () => {
    const newSlide = { ...BLANK_SLIDE, _key: `new-${Date.now()}`, order: slides.length + 1 };
    setSlides([...slides, newSlide]);
  };
  const updateSlide = (key, k, v) => setSlides(slides.map((s) => s._key === key ? { ...s, [k]: v } : s));
  const removeSlide = (key) => setSlides(slides.filter((s) => s._key !== key));

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-slideshow">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-serif text-3xl font-semibold text-navy">Hero Slideshow</h1>
          <p className="text-sm text-gray-500 mt-1">Sürükleyip bırakarak sıralayın · Görsel URL ya da yükle</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={addSlide} variant="outline" data-testid="admin-slide-add"><Plus size={16} className="mr-1" /> Slayt Ekle</Button>
          <Button onClick={save} className="bg-gold hover:bg-[#c49d58]" data-testid="admin-slideshow-save">Tümünü Kaydet</Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
        <h2 className="heading-serif font-semibold text-navy mb-3">Genel Ayarlar</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><Label className="text-xs">Varsayılan Süre (sn)</Label><Input type="number" value={settings.default_duration} onChange={(e) => setSettings({ ...settings, default_duration: Number(e.target.value) })} /></div>
          <div><Label className="text-xs">Geçiş Süresi (s)</Label><Input type="number" step="0.1" value={settings.transition_duration} onChange={(e) => setSettings({ ...settings, transition_duration: Number(e.target.value) })} /></div>
          <label className="flex items-center gap-2 pt-5"><input type="checkbox" checked={settings.autoplay} onChange={(e) => setSettings({ ...settings, autoplay: e.target.checked })} /> Otomatik oynat</label>
          <label className="flex items-center gap-2 pt-5"><input type="checkbox" checked={settings.pause_on_hover} onChange={(e) => setSettings({ ...settings, pause_on_hover: e.target.checked })} /> Hover'da duraklat</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={settings.show_arrows} onChange={(e) => setSettings({ ...settings, show_arrows: e.target.checked })} /> Oklar</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={settings.show_dots} onChange={(e) => setSettings({ ...settings, show_dots: e.target.checked })} /> Dots</label>
        </div>
      </div>

      <div className="space-y-4">
        <SortableList items={slides} setItems={setSlides} keyOf={(s) => s._key}>
          {(s, dragProps) => (
            <div className="bg-white rounded-xl border border-gray-100 p-5 mb-4" data-testid={`admin-slide-${s._key}`}>
              <div className="flex items-center justify-between mb-4 gap-2">
                <div className="flex items-center gap-2">
                  <DragHandle {...dragProps} />
                  <span className="text-xs bg-navy text-white rounded-full h-6 w-6 flex items-center justify-center">{slides.findIndex((x) => x._key === s._key) + 1}</span>
                  <h3 className="heading-serif font-semibold text-navy">Slayt</h3>
                  <label className="flex items-center gap-1.5 ml-3 text-xs text-gray-600">
                    <input type="checkbox" checked={s.active} onChange={(e) => updateSlide(s._key, "active", e.target.checked)} /> Aktif
                  </label>
                </div>
                <button onClick={() => removeSlide(s._key)} className="p-1.5 text-gray-500 hover:text-rose-600"><Trash2 size={14} /></button>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <ImageUpload value={s.image_desktop} onChange={(v) => updateSlide(s._key, "image_desktop", v)} label="Görsel (Desktop)" testId={`slide-${s._key}-img`} />
                <div>
                  <Label className="text-xs">Video URL (varsa)</Label>
                  <Input value={s.video_url} onChange={(e) => updateSlide(s._key, "video_url", e.target.value)} />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3 mt-3">
                <div><Label className="text-xs">Hizalama</Label>
                  <select className="w-full border rounded-md h-10 px-3 bg-softbg text-sm" value={s.align} onChange={(e) => updateSlide(s._key, "align", e.target.value)}>
                    <option value="left">Sol</option><option value="center">Orta</option><option value="right">Sağ</option>
                  </select>
                </div>
                <div><Label className="text-xs">Overlay Renk</Label><Input type="color" value={s.overlay_color} onChange={(e) => updateSlide(s._key, "overlay_color", e.target.value)} /></div>
                <div><Label className="text-xs">Overlay Opaklık</Label><Input type="number" step="0.05" min="0" max="1" value={s.overlay_opacity} onChange={(e) => updateSlide(s._key, "overlay_opacity", Number(e.target.value))} /></div>
              </div>

              <div className="grid md:grid-cols-2 gap-3 mt-3">
                <div><Label className="text-xs">Başlık (TR)</Label><Input value={s.title_tr} onChange={(e) => updateSlide(s._key, "title_tr", e.target.value)} /></div>
                <div><Label className="text-xs">Alt Başlık (TR)</Label><Textarea rows={2} value={s.subtitle_tr} onChange={(e) => updateSlide(s._key, "subtitle_tr", e.target.value)} /></div>
              </div>

              <div className="grid md:grid-cols-3 gap-3 mt-3">
                <div><Label className="text-xs">CTA1 Metin</Label><Input value={s.cta1_text} onChange={(e) => updateSlide(s._key, "cta1_text", e.target.value)} /></div>
                <div><Label className="text-xs">CTA1 Link</Label><Input value={s.cta1_link} onChange={(e) => updateSlide(s._key, "cta1_link", e.target.value)} /></div>
                <div><Label className="text-xs">CTA1 Stil</Label>
                  <select className="w-full border rounded-md h-10 px-3 bg-softbg text-sm" value={s.cta1_style} onChange={(e) => updateSlide(s._key, "cta1_style", e.target.value)}>
                    <option value="gold">Gold</option><option value="teal">Teal</option><option value="outline">Outline</option>
                  </select>
                </div>
                <div><Label className="text-xs">CTA2 Metin</Label><Input value={s.cta2_text} onChange={(e) => updateSlide(s._key, "cta2_text", e.target.value)} /></div>
                <div><Label className="text-xs">CTA2 Link</Label><Input value={s.cta2_link} onChange={(e) => updateSlide(s._key, "cta2_link", e.target.value)} /></div>
                <div><Label className="text-xs">CTA2 Stil</Label>
                  <select className="w-full border rounded-md h-10 px-3 bg-softbg text-sm" value={s.cta2_style} onChange={(e) => updateSlide(s._key, "cta2_style", e.target.value)}>
                    <option value="outline">Outline</option><option value="gold">Gold</option><option value="teal">Teal</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </SortableList>
      </div>
    </div>
  );
}
